import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiControlledNumericInput,
  MiControlledTextField,
  MiMenu,
  MiReorderButton,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import {
  IAreaTypeCategoryResponse,
  IAreaTypeResponse,
  IBidArea,
  IBidAreaMaterial,
  ICategoryFilterResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  mdiAccountOff,
  mdiCheck,
  mdiClose,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../../components/CollapsibleIcon/CollapsibleIcon";
import { ThinMaterialCard } from "../../../../../components/MaterialCard/ThinMaterialCard";
import { PlaceholderMaterial } from "../../../../../components/PlaceholderMaterial/PlaceholderMaterial";
import { useBidAreaViewModel } from "./useBidAreaViewModel";

export function BidArea({
  area,
  areaType,
  planAreaMaterials,
  isPreview,
  checkedAreas,
  selectedCategory,
  openMaterialResults,
  disableUp,
  disableDown,
  showCategoryFilter,
  openAreaDrawer,
  handleOpenItemDrawer,
  handlePlaceholder,
  handleCheckArea,
}: BidPlanAreaProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const { classes, theme, cx } = useStyles();
  const { control, watch, reset } = useForm({
    mode: "onChange",
    defaultValues: { label: area.label || "" },
  });
  const { label } = watch();

  const {
    dismissedCategories,
    handleRemoveItemFromArea,
    handleUpdateItemQuantity,
    handleRemoveSuppliedByBuilder,
    handleRemoveArea,
    handleUpdateAreaLabel,
    handleMoveArea,
    handleDismissCategory,
  } = useBidAreaViewModel();

  const categories = areaType?.categories || [];
  const isAreaSelected = checkedAreas.some((c) => c.id === area.id);
  const isPlaceHolderSelected = isAreaSelected
    ? selectedCategory?.categoryId
    : undefined;
  const isDisabled = showCategoryFilter || openAreaDrawer;

  // Get materials for this area from the pre-fetched list
  const areaMaterials = useMemo(() => {
    const materialIds = area.materials?.map((m) => m.materialId);
    return planAreaMaterials.filter((m) => materialIds?.includes(m.id));
  }, [area.materials, planAreaMaterials]);

  // Filter out placeholder categories that already have a material assigned
  const filteredCategories = useMemo(() => {
    const sorted = [...categories].sort((a, b) => {
      if (a.isRequired !== b.isRequired)
        return Number(b.isRequired) - Number(a.isRequired);
      return a.displayOrder - b.displayOrder;
    });
    const dismissed = dismissedCategories[area.id] || [];
    return sorted.filter(
      (cat) =>
        !areaMaterials.some((m) => m.category?.categoryId === cat.categoryId) &&
        !dismissed.includes(cat.categoryId)
    );
  }, [categories, areaMaterials, dismissedCategories, area.id]);

  const handleSaveLabel = () => {
    handleUpdateAreaLabel(area, label);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    reset({ label: area.label || "" });
    setIsEditing(false);
  };

  const getCardMenu = (
    areaMaterial: IBidAreaMaterial | undefined,
    item: IMaterialsResponse
  ) => {
    const options = [
      {
        label: "Remove Item",
        icon: mdiTrashCanOutline,
        action: () => handleRemoveItemFromArea(area, item.id),
      },
    ];

    if (areaMaterial?.suppliedByBuilder) {
      options.push({
        label: "Remove Supplied By Builder",
        icon: mdiAccountOff,
        action: () => handleRemoveSuppliedByBuilder(area, item.id),
      });
    }

    return options;
  };

  return (
    <Accordion
      expanded={isExpanded}
      disableGutters
      className={classes.accordion}
      style={{
        border: isAreaSelected
          ? `2px solid ${theme.palette.primary.accent}`
          : undefined,
      }}
    >
      <AccordionSummary className={classes.summary}>
        <Box className={cx(classes.headerBox, isPreview && classes.previewBox)}>
          <Box className={classes.rightHeaderBox}>
            {openMaterialResults && (
              <MiCheckbox
                value={isAreaSelected}
                handleChange={() => handleCheckArea(area, !isAreaSelected)}
                size="small"
                style={{ marginLeft: theme.spacing(-3) }}
              />
            )}
            <MiControlledTextField
              name="label"
              label={isEditing ? areaType?.name : ""}
              formControl={control}
              disabled={!isEditing}
              readOnly={!isEditing}
              className={classes.labelInput}
            />
          </Box>
          <Box className={classes.leftHeaderBox}>
            {isEditing ? (
              <Box className={classes.cancelSaveBox}>
                <MiButton
                  startIcon={{
                    path: mdiClose,
                    iconSize: 0.7,
                    iconColor: theme.palette.red.main,
                  }}
                  onClick={handleCancelEdit}
                />
                <MiButton
                  startIcon={{
                    path: mdiCheck,
                    iconSize: 0.7,
                    iconColor: theme.palette.green.main,
                  }}
                  onClick={handleSaveLabel}
                />
              </Box>
            ) : (
              <Box className={classes.leftActionBox}>
                {!isPreview && (
                  <>
                    <MiButton
                      title="Add Items"
                      variant={BUTTON_VARIANTS.TEXT}
                      startIcon={{ path: mdiPlus, iconSize: 0.8 }}
                      onClick={handleOpenItemDrawer}
                      disabled={isDisabled}
                      size="small"
                    />
                    <MiReorderButton
                      onMoveUp={() => handleMoveArea(area, "up")}
                      onMoveDown={() => handleMoveArea(area, "down")}
                      disableUp={disableUp}
                      disableDown={disableDown}
                      disabled={isDisabled}
                    />
                    <CollapsibleIcon
                      isCollapsed={isExpanded}
                      onCollapse={() => setIsExpanded((prev) => !prev)}
                    />
                  </>
                )}
              </Box>
            )}
            {!isPreview && (
              <MiMenu
                disabled={isEditing || isDisabled}
                data={[
                  {
                    label: "Edit Area Name",
                    icon: mdiPencilOutline,
                    action: () => setIsEditing(true),
                  },
                  {
                    label: "Remove Area",
                    icon: mdiTrashCanOutline,
                    action: () => handleRemoveArea(area),
                  },
                ]}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {areaMaterials.map((item) => {
          const areaMaterial = area.materials?.find(
            (m) => m.materialId === item.id
          );
          const menu = getCardMenu(areaMaterial, item);

          return (
            <ThinMaterialCard
              key={item.id}
              item={item}
              isSuppliedByBuilder={areaMaterial?.suppliedByBuilder}
              cardStyle={{ padding: 0 }}
              leftAction={
                <QuantityField
                  materialId={item.id}
                  quantity={areaMaterial?.quantity}
                  disabled={isDisabled}
                  area={area}
                  handleUpdateItemQuantity={handleUpdateItemQuantity}
                />
              }
              menuData={menu}
            />
          );
        })}
        {filteredCategories.map((cat) => (
          <PlaceholderMaterial
            key={cat.categoryId}
            category={cat}
            isSelected={isPlaceHolderSelected === cat.categoryId}
            disabled={isPreview || openAreaDrawer}
            preview={isPreview}
            status={undefined}
            onClick={() => handlePlaceholder(cat)}
            onDismiss={() => handleDismissCategory(cat.categoryId, area.id)}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

function QuantityField({
  materialId,
  quantity,
  disabled,
  area,
  handleUpdateItemQuantity,
}: QuantityFieldProps) {
  const { classes } = useStyles();
  const { control, reset } = useForm({
    mode: "onChange",
    defaultValues: { quantity },
  });

  useEffect(() => {
    reset({ quantity });
  }, [quantity]);

  return (
    <MiControlledNumericInput
      name="quantity"
      formControl={control}
      disabled={disabled}
      value={quantity}
      allowNegative={false}
      className={classes.inputQuantity}
      onBlur={(e: ChangeEvent<HTMLInputElement>) => {
        // if quantity is empty or 0, reset to 1
        if (!e.target.value || e.target.value === "0") {
          handleUpdateItemQuantity(area, materialId, 1);
        }
      }}
      onValueChange={({ floatValue }: { floatValue: number }) => {
        const qty = floatValue;
        handleUpdateItemQuantity(area, materialId, qty);
      }}
    />
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    boxShadow: "none",
    "&::before": { content: "none" },
  },
  summary: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 40,
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  previewBox: {
    opacity: 0.5,
  },
  rightHeaderBox: {
    display: "flex",
    alignItems: "center",
  },
  labelInput: {
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1),
    },
  },
  leftHeaderBox: {
    display: "flex",
    alignItems: "center",
  },
  cancelSaveBox: {
    display: "flex",
    gap: 1,
  },
  leftActionBox: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  inputQuantity: {
    width: 35,
    "& .MuiInputBase-input.MuiOutlinedInput-input": {
      textAlign: "center",
      padding: 0,
    },
    // hide up/down arrows in number input
    // Chrome, Safari, Edge, Opera
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    // Firefox
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
  },
}));

interface BidPlanAreaProps {
  area: IBidArea;
  areaType: IAreaTypeResponse | undefined;
  planAreaMaterials: IMaterialsResponse[];
  isPreview: boolean;
  checkedAreas: IBidArea[];
  selectedCategory: Partial<ICategoryFilterResponse>;
  openMaterialResults: boolean;
  disableUp: boolean;
  disableDown: boolean;
  showCategoryFilter: boolean;
  openAreaDrawer: boolean;
  handleCheckArea: (area: IBidArea, checked: boolean) => void;
  handleOpenItemDrawer: () => void;
  handlePlaceholder: (category: IAreaTypeCategoryResponse) => void;
}

interface QuantityFieldProps {
  materialId: string;
  quantity?: number;
  disabled: boolean;
  area: IBidArea;
  handleUpdateItemQuantity: (
    area: IBidArea,
    materialId: string,
    quantity: number
  ) => void;
}
